/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import {AccountLink, SFTPConnection} from '@ion/core/src/types/entities';
import {useSFTPConfigurations} from '@ion/query/src/hooks/useSFTPConfigurations';
import Dropdown, {createOptions, noneOption} from '../components/menus/Dropdown';
import SFTPConfigurationDetailView from './SFTPConfigurationDetailView';
import {SFTPConfigurationFields} from './SFTPConfigurationEditorUtils';

/**
 * The SFTPConfigurationChooserProps interface defines the set of props that
 * can be passed as parameters to the SFTPConfigurationChooser component.
 *
 * `onCTAClick` (Function, optional): A callback function triggered when the CTA link is clicked.
 * `accountId` (number, required): The account ID for which SFTP configurations are displayed.
 * `onChange` (Function, optional): A callback function triggered on value change.
 * `defaultValue` (string, optional): The default selected value.
 * `value` (string, optional): The currently selected value.
 */
export interface SFTPConfigurationChooserProps {
  onCTAClick?: Function;
  accountId: number;
  onChange?: Function;
  defaultValue?: string;
  value?: string;
  noneLabel?: string;
  includeNone?: boolean;
  account?: AccountLink;
  isView?: boolean;
  isCurrentTheme?: boolean;
  isCtaLink?: boolean;
  customLabel?: boolean;
  v2?: boolean;
};

/**
 * The SFTPConfigurationChooser component allows users to choose SFTP configurations
 * from a Dropdown. It displays a list of SFTP configurations related to a specific account.
 *
 * ```typescript
 *
 *  import {SFTPConfigurationChooser} from '@ion/dls/src/editors/';
 *
 *  <SFTPConfigurationChooser accountId={accountId} />
 *
 * ```
 */
export const SFTPConfigurationChooser = ({
  value,
  account,
  accountId,
  onChange,
  onCTAClick,
  defaultValue,
  isCurrentTheme = false,
  isView = false,
  isCtaLink = true,
  v2 = false,
  includeNone = true,
  customLabel = false,
  noneLabel = translate('common:options.none')
}: SFTPConfigurationChooserProps) => {
  const [selected, setSelected]             = useState(defaultValue || '');
  const [showDetailView, setShowDetailView] = useState(false);

  // useEffect to update the selected state when external value changes.
  useEffect(() => {
    if (!isEqual(value, selected)) {
      setSelected(
        value?.[SFTPConfigurationFields.Name] ||
        value || (selected === 'none' ? 'none' : undefined)
      );
    }
  }, [value]);

  // useSFTPConfigurations hook fetches SFTP configurations for the specified account.
  const {
    configurations,
    isLoading
  } = useSFTPConfigurations({accountId, enabled: !!accountId, v2});

  // useEffect to update the selected state when configurations change.
  useEffect(() => {
    if (!isLoading) {
      handleOnChange({
        target : {value: selected}
      });
    }
  }, [configurations, isLoading]);

  const options = useMemo(() => (
    noneOption({
      options : createOptions({
        items     : configurations,
        labelProp : SFTPConfigurationFields.Name,
        valProp   : SFTPConfigurationFields.Name
      }),
      label : noneLabel,
      value : 'none',
      includeNone
    })
  ), [configurations, noneLabel, includeNone]);

  const onCTALinkClick = useCallback(() => {
    setShowDetailView(true);
    onCTAClick?.();
  }, [onCTAClick]);

  const handleOnChange = useCallback(({target: {value}}) => {
    const selected = configurations?.find(
      configuration => configuration[SFTPConfigurationFields.Name] === value
    );
    onChange?.(selected);
    setSelected(selected?.[SFTPConfigurationFields.Name] || value);
  }, [configurations, onChange]);

  const onCancel = useCallback(() => {
    setShowDetailView(false);
  }, []);

  const onSelection = useCallback((selected: SFTPConnection) => {
    setSelected(selected?.[SFTPConfigurationFields.Name]);
    onChange?.(selected);
    setShowDetailView(false);
  }, [onChange]);

  return (
    <>
      <Dropdown fieldLabel={translate(`common:SFTPConfigurationChooser.${customLabel ? 'archiveLabel' : 'label'}`)}
        placeholder={translate('common:SFTPConfigurationChooser.placeholder')}
        ctaLinkText={translate('common:SFTPConfigurationChooser.ctaText')}
        onCTALinkClick={onCTALinkClick}
        defaultValue={defaultValue}
        onChange={handleOnChange}
        disabled={isLoading || isView}
        labelAsValue={false}
        options={options}
        value={isLoading ? translate('common:states.loading') : selected}
        hasCTALink={isCtaLink} />
      {showDetailView && (
        <SFTPConfigurationDetailView account={account}
          v2={v2}
          onCancel={onCancel}
          isCurrentTheme={isCurrentTheme}
          onSelection={onSelection} />
      )}
    </>
  );
};

export default SFTPConfigurationChooser;
